import { useCallback } from 'react';
import type {
    CombinedDiagnosticContext,
    MainProcessDiagnosticContext,
} from '@m43/bridge-interface';
import type { MapView } from '../esri/constructors';
import { useConnectionStatusDiagnostics } from '../connection-status/use-connection-status';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { getRendererDiagnosticContext } from './renderer-diagnostic-context';
import { getMapDiagnosticContext } from '../esri/helpers/map-diagnostic-context';

const IPC_TIMEOUT_MS = 5000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('IPC timeout')), ms);
        promise.then(
            (val) => {
                clearTimeout(timer);
                resolve(val);
            },
            (err) => {
                clearTimeout(timer);
                reject(err);
            }
        );
    });
}

async function fetchMainProcessContext(): Promise<{
    main: MainProcessDiagnosticContext | null;
    mainContextFailed: boolean;
    isElectron: boolean;
}> {
    const isElectron = !!electronBridge;

    if (isElectron && electronBridge && 'getDiagnosticContext' in electronBridge) {
        try {
            const main = await withTimeout(
                (electronBridge as {
                    getDiagnosticContext: () => Promise<MainProcessDiagnosticContext>;
                }).getDiagnosticContext(),
                IPC_TIMEOUT_MS
            );
            return { main, mainContextFailed: false, isElectron };
        } catch (err) {
            return { main: null, mainContextFailed: true, isElectron };
        }
    }

    return { main: null, mainContextFailed: false, isElectron };
}

type CollectDiagnosticContext = (
    mapView: MapView | undefined,
    mapRef: React.MutableRefObject<HTMLDivElement | null>,
    isEmbeddedMap: boolean
) => Promise<CombinedDiagnosticContext>;

/**
 * Returns a `collectDiagnosticContext` function that reads connection status
 * from the ConnectionStatusContext internally — callers don't need to thread
 * connection status through.
 */
export function useCollectDiagnosticContext(): CollectDiagnosticContext {
    const { status, history } = useConnectionStatusDiagnostics();

    return useCallback(
        async (
            mapView: MapView | undefined,
            mapRef: React.MutableRefObject<HTMLDivElement | null>,
            isEmbeddedMap: boolean
        ): Promise<CombinedDiagnosticContext> => {
            const renderer = getRendererDiagnosticContext(status, history);
            const map = getMapDiagnosticContext(mapView, mapRef, isEmbeddedMap);
            const { main, mainContextFailed, isElectron } = await fetchMainProcessContext();

            return {
                renderer,
                map,
                main,
                mainContextFailed,
                isElectron,
            };
        },
        [status, history]
    );
}
